import { drawRestartFrame, RESTART_DURATION, type RestartMemory } from '../animation/restartRenderer'
import { useCallback, useEffect, useRef } from 'react'
import { createBirthAudio, type BirthAudio } from '../animation/birthAudio'
import { BIRTH_TIMING, getBirthPhase } from '../animation/birthTimeline'
import { BirthFxRenderer } from '../animation/birthFx'

type UseBirthIntroOptions = {
  restartMemories: RestartMemory[] | null
  token: number | null
  reduceMotion: boolean
  soundEnabled: boolean
  onComplete: () => void
}

export function useBirthIntro({ token, restartMemories, reduceMotion, soundEnabled, onComplete }: UseBirthIntroOptions) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const completedRef = useRef(false)
  const animationFrameRef = useRef<number | null>(null)
  const timeoutRef = useRef<number | null>(null)
  const extraTimeoutsRef = useRef<Set<number>>(new Set())
  const audioRef = useRef<BirthAudio | null>(null)
  const birthFxRef = useRef<BirthFxRenderer | null>(null)
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete

  const finish = useCallback(() => {
    if (completedRef.current) return
    completedRef.current = true
    if (animationFrameRef.current !== null) window.cancelAnimationFrame(animationFrameRef.current)
    if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current)
    for (const timeout of extraTimeoutsRef.current) window.clearTimeout(timeout)
    extraTimeoutsRef.current.clear()
    birthFxRef.current?.stop()
    birthFxRef.current = null
    audioRef.current?.dispose()
    audioRef.current = null
    animationFrameRef.current = null
    timeoutRef.current = null
    onCompleteRef.current()
  }, [])

  useEffect(() => {
    if (token === null) return
    completedRef.current = false
    const app = document.querySelector<HTMLElement>('.app-shell')
    const previousInert = app?.inert ?? false
    if (app) app.inert = true
    containerRef.current?.querySelector<HTMLButtonElement>('button')?.focus()
    const canvas = canvasRef.current
    const context = canvas?.getContext('2d')
    const prelude = restartMemories !== null ? (reduceMotion ? 1400 : RESTART_DURATION) : 0
    const birthDuration = reduceMotion ? BIRTH_TIMING.reducedEnd : BIRTH_TIMING.fullEnd
    const duration = prelude + birthDuration
    const audio = createBirthAudio(reduceMotion || !soundEnabled)
    audioRef.current = audio
    const schedule = (callback: () => void, delay: number) => {
      const timeout = window.setTimeout(() => {
        extraTimeoutsRef.current.delete(timeout)
        callback()
      }, delay)
      extraTimeoutsRef.current.add(timeout)
    }

    const flash = (gold: boolean) => {
      const element = containerRef.current?.querySelector<HTMLElement>('.birth-flash')
      if (!element) return
      element.className = `birth-flash${gold ? ' gold' : ''} go`
      schedule(() => { element.className = `birth-flash${gold ? ' gold' : ''}` }, 600)
    }
    const shake = () => {
      const element = containerRef.current
      if (!element) return
      element.classList.remove('birth-overlay-shake')
      void element.offsetWidth
      element.classList.add('birth-overlay-shake')
      schedule(() => element.classList.remove('birth-overlay-shake'), 420)
    }
    const birthFx = reduceMotion
      ? null
      : new BirthFxRenderer({ flash, shake, schedule })
    birthFxRef.current = birthFx
    let cancelled = false
    let width = 0
    let height = 0
    let dpr = 1

    const resize = () => {
      if (!canvas) return
      const rect = containerRef.current?.getBoundingClientRect() ?? canvas.getBoundingClientRect()
      width = Math.max(1, rect.width)
      height = Math.max(1, rect.height)
      dpr = Math.min(2, Math.max(1, window.devicePixelRatio || 1))
      canvas.width = Math.round(width * dpr)
      canvas.height = Math.round(height * dpr)
      context?.setTransform(dpr, 0, 0, dpr, 0, 0)
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' && event.key !== 'Enter' && event.key !== ' ') return
      event.preventDefault()
      finish()
    }

    const setTimelineMarker = (elapsed: number) => {
      const element = containerRef.current
      if (!element) return
      element.dataset.birthElapsedMs = String(Math.round(elapsed))
      element.dataset.birthSkipReady = elapsed >= prelude + 500 ? 'true' : 'false'
      element.dataset.birthPhase = elapsed < prelude ? 'restart' : getBirthPhase(elapsed - prelude, reduceMotion)
    }
    setTimelineMarker(0)
    resize()
    window.addEventListener('resize', resize)
    window.addEventListener('keydown', onKeyDown)
    const startedAt = performance.now()
    let heartBurstFired = false
    let babyBurstFired = false
    let frameImpactFired = false
    let textImpactFired = false
    const draw = (now: number) => {
      if (cancelled || completedRef.current) return
      const elapsed = Math.min(duration, now - startedAt)
      setTimelineMarker(elapsed)
      if (elapsed < prelude) {
        if (context) drawRestartFrame(context, width, height, elapsed, restartMemories || [], reduceMotion)
        audio.syncRestart(elapsed)
      } else {
        context?.clearRect(0, 0, width, height)
        const birthElapsed = elapsed - prelude
        audio.sync(birthElapsed)
        if (birthFx && context) {
          const centerX = width / 2
          const centerY = height / 2
          if (!heartBurstFired && birthElapsed >= BIRTH_TIMING.heartbeatEnd) {
            heartBurstFired = true
            birthFx.heartBurst(centerX, centerY)
          }
          if (!babyBurstFired && birthElapsed >= BIRTH_TIMING.heartBurstEnd) {
            babyBurstFired = true
            birthFx.babyBurst(centerX, centerY)
          }
          if (!frameImpactFired && birthElapsed >= BIRTH_TIMING.babyEnd) {
            frameImpactFired = true
            birthFx.frameImpact(centerX, centerY)
          }
          if (!textImpactFired && birthElapsed >= BIRTH_TIMING.frameEnd) {
            textImpactFired = true
            flash(true)
            // Four staggered impacts keep the original two-sided dive-bomb
            // composition, but make the landing moment denser and more
            // explosive on a phone-sized viewport.
            const missiles = [
              { x0: centerX - 360, y0: -280, x1: centerX - 100, y1: centerY - 58, speed: 0.026, delay: 120 },
              { x0: centerX + 380, y0: -300, x1: centerX + 100, y1: centerY - 52, speed: 0.024, delay: 360 },
              { x0: centerX - 210, y0: -360, x1: centerX - 58, y1: centerY + 58, speed: 0.029, delay: 560 },
              { x0: centerX + 230, y0: -340, x1: centerX + 58, y1: centerY + 52, speed: 0.028, delay: 760 }
            ]
            for (const missile of missiles) {
              schedule(() => birthFx.launchMissile(missile.x0, missile.y0, missile.x1, missile.y1, missile.speed, () => birthFx.missileImpact(missile.x1, missile.y1)), missile.delay)
            }
          }
          birthFx.render(context)
        }
      }
      if (elapsed >= duration) {
        finish()
        return
      }
      animationFrameRef.current = window.requestAnimationFrame(draw)
    }
    animationFrameRef.current = window.requestAnimationFrame(draw)
    timeoutRef.current = window.setTimeout(finish, duration + 120)

    return () => {
      cancelled = true
      if (app) app.inert = previousInert
      app?.querySelector<HTMLButtonElement>('.back-button')?.focus()
      if (animationFrameRef.current !== null) window.cancelAnimationFrame(animationFrameRef.current)
      if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current)
      for (const timeout of extraTimeoutsRef.current) window.clearTimeout(timeout)
      extraTimeoutsRef.current.clear()
      birthFx?.stop()
      if (birthFxRef.current === birthFx) birthFxRef.current = null
      audio.dispose()
      if (audioRef.current === audio) audioRef.current = null
      animationFrameRef.current = null
      timeoutRef.current = null
      window.removeEventListener('resize', resize)
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [finish, reduceMotion, soundEnabled, token, restartMemories])

  return { containerRef, canvasRef, finish }
}
